'use client'

import ConfirmationDialog from '@/components/ui/confirmation-dialog'
import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  NavigationGuardContext,
  type NavigationGuard,
  type NavigationGuardDialogOptions,
} from './navigation-guard-context'

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('common.unsavedChanges')
  const [open, setOpen] = useState(false)
  const [dialog, setDialog] = useState<NavigationGuardDialogOptions>()
  const guardRef = useRef<NavigationGuard | null>(null)
  const pendingNavigationRef = useRef<(() => void) | null>(null)

  const setNavigationGuard = useCallback((guard: NavigationGuard | null) => {
    guardRef.current = guard
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) pendingNavigationRef.current = null
    setOpen(nextOpen)
  }, [])

  const confirmNavigation = useCallback(() => {
    const navigate = pendingNavigationRef.current
    pendingNavigationRef.current = null
    setOpen(false)
    navigate?.()
  }, [])

  const requestNavigation = useCallback((navigate: () => void) => {
    const guard = guardRef.current
    const leave = () => {
      guard?.onBeforeCancel?.()
      navigate()
    }
    pendingNavigationRef.current = null
    if (!guard?.isBlocked()) {
      setOpen(false)
      leave()
      return
    }

    pendingNavigationRef.current = () => {
      guard.discard?.()
      leave()
    }
    setDialog(guard.dialog)
    setOpen(true)
  }, [])

  const value = useMemo(() => ({ requestNavigation, setNavigationGuard }), [requestNavigation, setNavigationGuard])

  return (
    <NavigationGuardContext value={value}>
      {children}
      <ConfirmationDialog
        open={open}
        onOpenChange={handleOpenChange}
        variant="warning"
        title={dialog?.title ?? t('title')}
        description={dialog?.description ?? t('description')}
        actions={[
          { label: dialog?.cancelLabel ?? t('cancel'), variant: 'secondary', onClick: () => handleOpenChange(false) },
          {
            label: dialog?.confirmLeavingLabel ?? t('leaveWithoutSaving'),
            onClick: confirmNavigation,
          },
        ]}
      />
    </NavigationGuardContext>
  )
}
